import React, { useMemo } from 'react';

import { SubscriptionCreate } from 'App/components/Subscriptions/SubscriptionsForm';

import { Link, TypographyListItem, Typography } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import DataGrid from '@gogame/data-grid';
import { getColor } from '../Status';
import { AmountFormatter, NumberValueFormatter } from '@gogame/intl-ts';
import {
  useScreenDispatch,
  useScreenGo,
  useScreenState,
  useScreenTitle,
} from '@gogame/screens';

import AffiliateDetails from 'scenes/Affiliates/components/AffiliateDetails';
import BusinessPartnerDetails from 'scenes/BusinessPartners/components/BusinessPartnerDetails';
import {
  IoFormEdit,
  AmendmentFormCreate,
  IOFormCreate,
} from 'scenes/IO/components/IOForm';
import {
  COLUMN_ID,
  COLUMN_BUSINESS_ENTITIES,
  COLUMN_START_DATE,
  COLUMN_STATUS,
  COLUMN_TOTAL_BUDGET,
  COLUMN_MONTHLY_BUDGET,
  COLUMN_AGREEMENT_TYPE,
  COLUMN_EXPENSE_TYPE,
  COLUMN_CUSTOMER_COMPANY,
  COLUMN_APPROVERS,
  COLUMN_CREATED,
  COLUMN_LAST_UPDATED,
  COLUMN_CREATED_BY,
  COLUMN_OWNER,
  IO_PAGE,
  COLUMN_EXPIRATION_DATE,
} from 'scenes/IO/constants';
import { useLoadIos, useIsUpdating } from 'scenes/IO/data';
import { useExportIo } from 'scenes/IO/hooks';
import { useDownloadTemplate } from 'scenes/IO/hooks';
import {
  SET_TABLE_SETTINGS,
  COLLAPSE_COLUMNS,
  REORDER_COLUMNS,
} from 'scenes/IO/screenReducer';

import IoActions from '../Actions';

import { useFiltersWithPeriodType } from './useFiltersWithPeriodType';

import { ScreenLink } from '@gogame/screens';

import { ioDetailsLink } from 'scenes/IO/navigation';
import MonthlyBudget from '../MonthlyBudget';
import IoToolbar from './Toolbar';
import ActionStatus from 'scenes/IO/components/Approver/ActionStatus';

const IoDetailsLink = ({ value }) => {
  return (
    <ScreenLink
      link={ioDetailsLink}
      pathParams={{
        id: value,
      }}
    >
      {value}
    </ScreenLink>
  );
};

const IODataTable = () => {
  const [childComponent, openChildComponent] = useChildComponent();
  const dispatch = useScreenDispatch();
  const screenState = useScreenState();
  const [screenTitle] = useScreenTitle();
  const go = useScreenGo();
  const isUpdating = useIsUpdating();
  const downloadTemplate = useDownloadTemplate();

  const filters = useFiltersWithPeriodType(screenState.filters);
  const { tableSettings, searchQuery } = screenState;
  const params = useMemo(
    () => ({ filters, tableSettings, searchQuery }),
    [filters, tableSettings, searchQuery]
  );

  const { data, isLoading, isRefetching } = useLoadIos(params);
  const exportIoTable = useExportIo();

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleEdit = (io) => {
    openChildComponent(IoFormEdit, {
      id: io.id,
    });
  };

  const handleAddAmendment = (io) => {
    openChildComponent(AmendmentFormCreate, {
      id: io.id,
    });
  };

  const openBusinessPartner = (bpId) => {
    openChildComponent(BusinessPartnerDetails, {
      id: bpId,
    });
  };

  const openAffiliate = (affiliateId) => {
    openChildComponent(AffiliateDetails, {
      id: affiliateId,
    });
  };

  const handleExport = async () => {
    await exportIoTable(params);
  };

  const handleOpenSubscriptionsForm = () => {
    openChildComponent(SubscriptionCreate, {
      page: IO_PAGE,
      withPayTo: false,
      payload: params,
    });
  };

  const handleCreate = () => {
    openChildComponent(IOFormCreate, {
      onClose: ({ id } = {}) => {
        if (id) {
          go(id);
        }
      },
    });
  };

  const handleOpenLink = async () => {
    await downloadTemplate();
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  return (
    <React.Fragment>
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        title={screenTitle}
        loading={isLoading}
        fetching={isRefetching || isUpdating}
        ready={screenState.ready}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        toolbar={
          <IoToolbar
            onExport={handleExport}
            onOpenSubscriptionsForm={handleOpenSubscriptionsForm}
            onCreate={handleCreate}
            onOpenLink={handleOpenLink}
          />
        }
        storageKey={'ioManagement'}
        filtered={!!screenState.searchQuery}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'link'}
          headerName={'ID'}
          align={'right'}
          headerAlign={'right'}
          width={80}
          fixed={true}
          resizable={false}
          linkComponent={IoDetailsLink}
        />
        <DataGrid.Column
          field={COLUMN_START_DATE}
          headerName={'Start Date'}
          width={120}
          type={'date'}
        />
        <DataGrid.Column
          field={COLUMN_EXPIRATION_DATE}
          headerName={'Expiration Date'}
          width={120}
          type={'date'}
        />
        <DataGrid.Column
          field={COLUMN_BUSINESS_ENTITIES}
          headerName={'BP / Affiliate'}
          width={320}
          type={'list'}
          flexGrow={1}
          getItemValue={(entity) => {
            const handleOpen = () => {
              if (entity.level === 0) {
                openBusinessPartner(entity.id);
              } else {
                openAffiliate(entity.id);
              }
            };

            return (
              <Link key={entity.id} onClick={() => handleOpen(entity.id)}>
                <TypographyListItem> {entity.name} </TypographyListItem>
              </Link>
            );
          }}
        />
        <DataGrid.Column
          field={COLUMN_STATUS}
          headerName={'Status'}
          type={'status'}
          width={170}
          getStatusColor={getColor}
        />

        <DataGrid.Column
          field={COLUMN_TOTAL_BUDGET}
          headerName={'Total Budget'}
          width={110}
          flexGrow={1}
          sortable={false}
          getValue={(_, row) => {
            const budget = row?.budget;
            if (budget?.revShareBudget?.rateOfNet) {
              return null;
            }
            return budget?.total?.amount;
          }}
          type={'amount'}
        />
        <DataGrid.Column
          field={COLUMN_MONTHLY_BUDGET}
          headerName={'Monthly Budget'}
          width={110}
          flexGrow={1}
          sortable={false}
          type={'text'}
          align={'right'}
          headerAlign={'right'}
          formatter={(_, row) => {
            const budget = row?.budget;
            const { monthly, revShareBudget = {}, isCustomized } = budget;

            if (isCustomized) {
              return (
                <MonthlyBudget monthly={monthly} limit={1} variant={'body2'} />
              );
            }

            if (revShareBudget.rateOfNet) {
              return (
                <NumberValueFormatter.Percent
                  value={revShareBudget.rateOfNet}
                />
              );
            }

            return <AmountFormatter {...monthly[0].amount} />;
          }}
        />
        <DataGrid.Column
          field={COLUMN_AGREEMENT_TYPE}
          headerName={'Agreement Type'}
          width={132}
          type={'text'}
          getTooltip={() => ''}
          formatter={(value, row) => {
            return (
              <React.Fragment>
                {value.name}
                {row.parentIo ? (
                  <Typography
                    as={'span'}
                    sx={{ display: 'block' }}
                    color={'text.secondary'}
                    variant={'inherit'}
                  >
                    Amendment
                  </Typography>
                ) : null}
              </React.Fragment>
            );
          }}
        />
        <DataGrid.Column
          field={COLUMN_EXPENSE_TYPE}
          headerName={'Expense Type'}
          width={132}
          type={'text'}
          getValue={(v) => v?.name}
          getTooltip={() => ''}
        />

        <DataGrid.Column
          field={COLUMN_CUSTOMER_COMPANY}
          type={'text'}
          headerName={'Customer Company'}
          align={'left'}
          headerAlign={'left'}
          width={320}
          getValue={(v) => v?.name}
        />
        <DataGrid.Column
          field={COLUMN_APPROVERS}
          headerName={'Approvers'}
          width={160}
          type={'list'}
          limit={1}
          sortable={false}
          getItemValue={getApproverLabel}
          getItemTooltip={getApproverLabel}
          getItemIcon={(opt) => {
            const { isPredicted, signStatus } = opt;
            return isPredicted ? null : <ActionStatus actionId={signStatus} />;
          }}
        />
        <DataGrid.Column
          field={COLUMN_CREATED}
          headerName={'Created'}
          width={135}
          type={'date'}
          template={'LLL'}
        />
        <DataGrid.Column
          field={COLUMN_LAST_UPDATED}
          headerName={'Last Updated'}
          width={135}
          type={'date'}
          template={'LLL'}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_BY}
          headerName={'Created by'}
          width={150}
          type={'text'}
        />
        <DataGrid.Column
          field={COLUMN_OWNER}
          headerName={'Owner'}
          width={160}
          type={'text'}
          getValue={(v) => v?.name}
        />
        <DataGrid.Column
          limit={3}
          actionsColDef
          type={'actions'}
          actionsComponent={IoActions}
          onEdit={handleEdit}
          onAddAmendment={handleAddAmendment}
        />
      </DataGrid>
      {childComponent}
    </React.Fragment>
  );
};

function getApproverLabel(opt) {
  return opt.name || opt.role;
}

export default IODataTable;
